import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { useAccess } from '@hooks/access';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { version } from '../../../package.json';

const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

export const UpdateSW = () => {
  const { t } = useTranslation('app');
  const access = useAccess();
  const [hasUpdate, setHasUpdate] = useState(false);
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();

  const handleUpdate = () => window.location.reload();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent<{ type?: string; version?: string }>) => {
      if (event.data?.type !== 'version' || !event.data.version) return;
      if (event.data.version !== version) setHasUpdate(true);
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const checkForUpdate = () => {
      if (document.visibilityState !== 'visible') return;
      navigator.serviceWorker
        .getRegistration()
        .then((registration) => registration?.update())
        .catch(() => {});
    };

    const interval = window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', checkForUpdate);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', checkForUpdate);
    };
  }, []);

  useEffect(() => {
    if (!hasUpdate || !access.fieldUser || isFetching || isMutating) return;
    handleUpdate();
  }, [hasUpdate, access.fieldUser, isFetching, isMutating]);

  return (
    <AlertDialog open={hasUpdate}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('navigation.update.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('navigation.update.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button onClick={handleUpdate}>{t('actions.update')}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
